import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Form, 
  Input, 
  Table, 
  Tooltip, 
  Pagination, 
  Card, 
  message, 
  Modal,
  Space,
  Descriptions,
  Row,
  Col,
  Statistic,
  Collapse,
  Empty,
  Spin,
  Typography
} from 'antd';
import { 
  SearchOutlined, 
  FilePdfOutlined, 
  ExclamationCircleOutlined, 
  RobotOutlined,
  WarningOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'antd/dist/reset.css'; // 仅为此页面导入 Ant Design 样式
import API from '../services/api'; // 导入统一的API实例
import { saveAs } from 'file-saver';
import { useLocation } from 'react-router-dom';
import usePermissionCheck from '../hooks/usePermissionCheck';
import PermissionDenied from '../components/PermissionDenied';

const { Paragraph } = Typography;

const UserAttackQuery = () => {
  const { hasPermission, isLoading: permissionLoading, userAccountType, userPermissions } = usePermissionCheck('canViewAttackRecords');
  
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const ipFromUrl = params.get('ip') || '';
  const [ip, setIp] = useState(ipFromUrl);
  const [attackLogs, setAttackLogs] = useState([]); // 存储攻击日志数据
  const [loading, setLoading] = useState(false); // 控制加载动画
  const [currentPage, setCurrentPage] = useState(1); // 当前页码
  const [exportLoading, setExportLoading] = useState(false); // 控制导出按钮加载状态
  const [showExportModal, setShowExportModal] = useState(false); // 控制导出确认弹窗
  const logsPerPage = 10; // 每页显示的日志数量
  const [hasSearched, setHasSearched] = useState(false); // 控制是否显示查询结果

  // AI分析相关状态
  const [aiAnalysisModal, setAiAnalysisModal] = useState({
    visible: false,
    loading: false,
    analyzing: false,
    data: null
  });
  const [showAnalysisConfirm, setShowAnalysisConfirm] = useState(false);

  // 检查是否有导出权限 - 主账号默认有所有权限
  const hasExportPermission = userAccountType === 'main' || userPermissions?.canExportReports || false;

  // IP地址格式验证
  const validateIP = (ip) => {
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipRegex.test(ip);
  };

  useEffect(() => {
    setIp(ipFromUrl);
  }, [ipFromUrl]);

  // 权限检查 - 移到所有hooks之后
  if (!hasPermission) {
    return (
      <PermissionDenied 
        isLoading={permissionLoading}
        permissionName="攻击记录"
        userAccountType={userAccountType}
      />
    );
  }

  // 查询攻击日志的处理函数
  const handleAttackQuery = async () => {
    const trimmedIp = ip.trim();
    
    if (!trimmedIp) {
      message.error('请输入IP地址');
      return;
    }
    
    if (!validateIP(trimmedIp)) {
      message.error('请输入正确的IP地址格式（如：192.168.1.1）');
      return;
    }

    setLoading(true); // 开启加载动画
    setHasSearched(false); // 重置查询状态
    try {
      // 调用API获取攻击日志数据
      const response = await API.get('/api/user/ip-attack-lookup', {
        params: { ip: trimmedIp },  // 传递验证过的IP地址作为查询参数
      });

      // 检查响应数据结构
      if (response.data.message && !response.data.attackLogs) {
        // 后端返回消息表示没有找到数据
        message.info({
          content: `未找到IP地址 ${trimmedIp} 的攻击日志记录`,
          duration: 4
        });
        setAttackLogs([]);
      } else {
        const logs = response.data.attackLogs || [];
        if (logs.length === 0) {
          message.info({
            content: `未找到IP地址 ${trimmedIp} 的攻击日志记录`,
            duration: 4
          });
          setAttackLogs([]);  // 如果没有匹配的数据，清空attackLogs
        } else {
          setAttackLogs(logs); // 保存匹配到的日志数据
          setCurrentPage(1); // 每次查询后重置页码为1
          message.success(`查询成功，找到 ${logs.length} 条攻击日志`);
        }
      }
      setHasSearched(true); // 标记为已进行查询
    } catch (error) {
      console.error('获取攻击日志时出错:', error);
      
      // 直接显示后端返回的错误内容
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else if (error.request) {
        message.error('网络连接失败，请检查网络状态');
      } else {
        message.error('查询出错，请稍后重试');
      }
      
      // 发生真正错误时也要标记为已搜索，以便显示错误状态
      setHasSearched(true);
      setAttackLogs([]);
    }
    setLoading(false); // 关闭加载动画
  };

  // 修改持续时间转换函数，确保秒数显示
  const convertDurationToChineseWithSeconds = (duration) => {
    // 支持格式如 "1day2hours3minutes4seconds"
    const regex = /(\d+)(days?|hours?|minutes?|seconds?)/g;
    let result = '';
    let match;
    while ((match = regex.exec(duration)) !== null) {
      const value = match[1];
      const unit = match[2];
      switch (unit) {
        case 'day':
        case 'days':
          result += `${value}天 `;
          break;
        case 'hour':
        case 'hours':
          result += `${value}小时 `;
          break;
        case 'minute':
        case 'minutes':
          result += `${value}分钟 `;
          break;
        case 'second':
        case 'seconds':
          result += `${value}秒`;
          break;
        default:
          result += match[0];
      }
    }
    return result.trim() || duration;
  };

  // 将Mbps流量转换为Gbps
  const convertToGbps = (sizeInMbps) => {
    return (sizeInMbps / 1000).toFixed(2); // 转换为Gbps，保留两位小数
  };

  const formatDateTimeWithoutMilliseconds = (dateTimeStr) => {
    return dateTimeStr.split('.')[0];  // 只取.之前的部分，去掉毫秒
  };  

  // 将字节流量转换为Gbps
  const convertToGbpsFromBytes = (sizeInBytes) => {
    const sizeInBits = sizeInBytes * 8; // 转换为比特
    const sizeInMbps = sizeInBits / 1e6; // 转换为Mbps
    return (sizeInMbps / 1000).toFixed(2); // 转换为Gbps，保留两位小数
  };

  // 计算拦截率
  const calculateInterceptRate = (size, dropSize) => {
    const peakTrafficGbps = convertToGbps(size);
    const dropTrafficGbps = convertToGbpsFromBytes(dropSize);
    
    if (peakTrafficGbps === 0) return '0.00%';
    
    // 计算拦截率并保留两位小数，不进行进位
    let interceptRate = (dropTrafficGbps / peakTrafficGbps) * 100;
    
    // 将拦截率转换为字符串，并保留两位小数，防止进位
    interceptRate = interceptRate.toString();
    const dotIndex = interceptRate.indexOf('.');
    
    // 如果有小数点，截取小数点后两位；如果没有小数点，则补充".00"
    if (dotIndex !== -1) {
      interceptRate = interceptRate.substring(0, dotIndex + 3); // 保留两位小数
    } else {
      interceptRate = interceptRate + '.00'; // 没有小数点时补充.00
    }
    
    return `${interceptRate}%`;
  };

  // 分页逻辑，显示所有日志
  const indexOfLastLog = currentPage * logsPerPage;
  const indexOfFirstLog = indexOfLastLog - logsPerPage;
  const currentLogs = attackLogs.slice(indexOfFirstLog, indexOfLastLog);

  const totalPages = Math.ceil(attackLogs.length / logsPerPage);

  // 导出PDF功能
  const handleExportClick = () => {
    if (attackLogs.length === 0) {
      message.error('没有数据可生成');
      return;
    }
    
    // 检查是否有导出权限
    if (!hasExportPermission) {
      message.error('您的RAM账号没有导出攻击报告的权限，请联系主账号管理员申请相关权限');
      return;
    }
    
    setShowExportModal(true);
  };

  const handleExportConfirm = () => {
    setShowExportModal(false);
    setExportLoading(true);
    
    // 调用后端API进行PDF导出
    const exportData = {
      ip: ip,
      exportType: 'full',
      compress: true
    };
    
    // 使用Blob API处理二进制响应
    API.post('/api/user/export-attack-logs-pdf', exportData, { responseType: 'blob' })
      .then(response => {
        // 创建下载链接
        const blob = new Blob([response.data], { type: 'application/pdf' });
        
        // 获取当前北京时间用于文件名
        const date = new Date();
        const beijingTime = new Date(date.getTime() + (8 * 60 * 60 * 1000)); // 转换为北京时间
        const formattedDate = `${beijingTime.getUTCFullYear()}-${String(beijingTime.getUTCMonth() + 1).padStart(2, '0')}-${String(beijingTime.getUTCDate()).padStart(2, '0')}`;
        
        // 生成6位随机字符
        const randomStr = Math.random().toString(36).substring(2, 8);
        
        const fileName = `DDoS_Attack_Report_${ip.replace(/\./g, '_')}_${formattedDate}_${randomStr}.pdf`;
        
        // 使用file-saver库保存文件
        saveAs(blob, fileName);
        message.success('PDF报告生成成功');
        
        setExportLoading(false);
      })
      .catch(error => {
        console.error('导出PDF失败:', error);
        let errorMsg = '导出PDF失败';
        
        // 尝试解析错误消息（如果是JSON格式）
        if (error.response && error.response.data) {
          try {
            // 对于Blob类型的错误响应，需要先读取内容
            const reader = new FileReader();
            reader.onload = function() {
              try {
                const errorData = JSON.parse(reader.result);
                message.error(errorData.message || errorMsg);
              } catch (e) {
                message.error(errorMsg);
              }
            };
            reader.readAsText(error.response.data);
          } catch (e) {
            message.error(errorMsg);
          }
        } else {
          message.error(errorMsg);
        }
        
        setExportLoading(false);
      });
  };

  // AI分析功能 - 显示确认对话框
  const handleAIAnalysisClick = () => {
    if (attackLogs.length === 0) {
      message.error('没有攻击记录可分析');
      return;
    }
    setShowAnalysisConfirm(true);
  };

  // 确认后开始分析
  const handleConfirmAnalysis = async () => {
    setShowAnalysisConfirm(false);
    setAiAnalysisModal({ visible: true, loading: false, analyzing: true, data: null });

    try {
      const endDate = dayjs();
      const startDate = dayjs().subtract(3, 'day');

      const response = await API.post('/api/attack-analysis/analyze', {
        targetIP: ip,
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD')
      });

      if (response.data.code === 0) {
        // 根据是否来自缓存显示不同的提示
        if (response.data.fromCache) {
          message.success(`AI分析完成（缓存数据，${response.data.cacheExpireIn}分钟后过期）`);
        } else {
          message.success('AI分析完成');
        }
        
        // 将analysisId和缓存信息添加到data中
        const analysisData = {
          ...response.data.data,
          analysisId: response.data.analysisId,
          fromCache: response.data.fromCache,
          cacheExpireIn: response.data.cacheExpireIn,
          cacheInfo: response.data.cacheInfo
        };
        setAiAnalysisModal({
          visible: true,
          loading: false,
          analyzing: false,
          data: analysisData
        });
      } else {
        message.error(response.data.message || 'AI分析失败');
        setAiAnalysisModal({ visible: false, loading: false, analyzing: false, data: null });
      }
    } catch (error) {
      console.error('AI分析失败:', error);
      message.error(error.response?.data?.message || 'AI分析失败');
      setAiAnalysisModal({ visible: false, loading: false, analyzing: false, data: null });
    }
  };

  const exportAnalysisPDF = async () => {
    try {
      message.loading({ content: '正在生成PDF...', key: 'pdf' });
      
      // 从响应中获取analysisId
      const analysisId = aiAnalysisModal.data.analysisId || aiAnalysisModal.data._id;
      
      const response = await API.get(`/api/attack-analysis/${analysisId}/export-pdf`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `attack_analysis_${ip}_${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success({ content: 'PDF导出成功', key: 'pdf' });
    } catch (error) {
      console.error('导出PDF失败:', error);
      message.error({ content: '导出PDF失败', key: 'pdf' });
    }
  };


  // 表格列定义
  const columns = [
    {
      title: '目标IP',
      dataIndex: 's_ip',
      key: 's_ip',
      width: 130,
      align: 'center',
    },
    {
      title: '目的端口',
      dataIndex: 's_port_list',
      key: 's_port_list',
      width: 90,
      align: 'center',
      render: (text) => (
        <Tooltip title={text} placement="top">
          <span style={{ cursor: 'pointer' }}>
            {text.length > 8 ? `${text.slice(0, 6)}...` : text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: '攻击源IP',
      dataIndex: 'c_ip_list',
      key: 'c_ip_list',
      width: 120,
      align: 'center',
      render: (text) => (
        <Tooltip title={text} placement="top">
          <span style={{ cursor: 'pointer' }}>
            {text.length > 15 ? `${text.slice(0, 12)}...` : text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: '源端口',
      dataIndex: 'c_port_list',
      key: 'c_port_list',
      width: 90,
      align: 'center',
      render: (text) => (
        <Tooltip title={text} placement="top">
          <span style={{ cursor: 'pointer' }}>
            {text.length > 8 ? `${text.slice(0, 6)}...` : text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: '攻击状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      align: 'center',
      render: (status) => (
        <span 
          style={{ 
            color: status === 1 ? '#ff4d4f' : '#52c41a',
            fontWeight: 500
          }}
        >
          {status === 1 ? '正在攻击' : '攻击结束'}
        </span>
      ),
    },
    {
      title: '防护方式',
      dataIndex: 'deftype',
      key: 'deftype',
      width: 250,
      align: 'center',
    },
    {
      title: '服务器峰值流量',
      dataIndex: 'size',
      key: 'size',
      width: 120,
      align: 'center',
      render: (size) => (
        <span style={{ fontWeight: 500, color: '#1890ff' }}>
          {convertToGbps(size)} Gbps
        </span>
      ),
    },
    {
      title: '拦截峰值流量',
      dataIndex: 'drop_size',
      key: 'drop_size',
      width: 120,
      align: 'center',
      render: (dropSize) => (
        <span style={{ fontWeight: 500, color: '#52c41a' }}>
          {convertToGbpsFromBytes(dropSize)} Gbps
        </span>
      ),
    },
    {
      title: '拦截率',
      key: 'intercept_rate',
      width: 90,
      align: 'center',
      render: (_, record) => {
        const rate = calculateInterceptRate(record.size, record.drop_size);
        const percentage = parseFloat(rate.replace('%', ''));
        const color = percentage > 80 ? '#52c41a' : percentage > 50 ? '#faad14' : '#ff4d4f';
        return (
          <span style={{ fontWeight: 500, color }}>
            {rate}
          </span>
        );
      },
    },
    {
      title: '包类型',
      dataIndex: 'packet_type',
      key: 'packet_type',
      width: 90,
      align: 'center',
    },
    {
      title: '开始时间',
      dataIndex: 'start_time',
      key: 'start_time',
      width: 160,
      align: 'center',
      render: (time) => formatDateTimeWithoutMilliseconds(time),
    },
    {
      title: '结束时间',
      dataIndex: 'end_time',
      key: 'end_time',
      width: 160,
      align: 'center',
    },
    {
      title: '持续时间',
      dataIndex: 'duration',
      key: 'duration',
      width: 130,
      align: 'center',
      render: (duration) => (
        <span style={{ fontWeight: 500 }}>
          {convertDurationToChineseWithSeconds(duration)}
        </span>
      ),
    },
  ];

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      handleAttackQuery();
    }
  };

  return (
    <div style={{ 
      padding: '20px', 
      backgroundColor: '#fafafa', 
      minHeight: 'calc(100vh - 64px)'
    }}>
      <div style={{ maxWidth: '100%', margin: '0 auto' }}>
        {/* 页面标题 */}
        <div style={{ 
          textAlign: 'center', 
          marginBottom: '20px',
          background: '#fff',
          padding: '16px 20px',
          borderRadius: '8px',
          border: '1px solid #e8e8e8',
          maxWidth: '600px',
          margin: '0 auto 20px auto'
        }}>
          <h1 style={{ 
            margin: 0, 
            fontSize: '24px', 
            fontWeight: '500',
            color: '#262626'
          }}>
            攻击记录查询
          </h1>
        </div>

        {/* 提示信息 */}
        <Card 
          style={{ 
            marginBottom: '12px',
            border: '1px solid #91d5ff',
            background: '#e6f7ff',
            maxWidth: '600px',
            margin: '0 auto 12px auto'
          }}
          styles={{ body: { padding: '8px 16px' } }}
        >
          <div style={{ 
            textAlign: 'center', 
            color: '#0050b3',
            fontSize: '14px'
          }}>
            📅 查询数据为近三天数据
          </div>
        </Card>
        
        {/* 搜索区域 */}
        <Card 
          style={{ 
            marginBottom: '20px',
            border: '1px solid #e8e8e8',
            maxWidth: '600px',
            margin: '0 auto 20px auto'
          }}
          styles={{ body: { padding: '16px' } }}
        >
          <Form layout="inline" style={{ justifyContent: 'center' }}>
            <Form.Item>
                              <Input.Search
                  placeholder="请输入要查询的IP地址"
                  value={ip}
                  onChange={(e) => setIp(e.target.value)}
                  onSearch={handleAttackQuery}
                  onKeyPress={handleSearch}
                  loading={loading}
                  style={{ width: '320px' }}
                  size="large"
                  enterButton={
                    <Button 
                      type="primary" 
                      size="large"
                      icon={<SearchOutlined />}
                      loading={loading}
                      style={{
                        transition: 'all 0.3s ease',
                        transform: loading ? 'scale(0.95)' : 'scale(1)'
                      }}
                    >
                      {loading ? '查询中...' : '查询'}
                    </Button>
                  }
                />
            </Form.Item>
          </Form>
        </Card>

        {/* 结果区域 */}
        {hasSearched && (
          <Card
            style={{ border: '1px solid #e8e8e8' }}
            styles={{ body: { padding: '20px' } }}
          >
            {attackLogs.length > 0 && (
              <div style={{ 
                marginBottom: '20px', 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '12px 16px',
                background: '#f5f5f5',
                borderRadius: '6px'
              }}>
                <div style={{ 
                  color: '#595959', 
                  fontSize: '14px'
                }}>
                  总计 <strong>{attackLogs.length}</strong> 条记录，
                  当前显示第 <strong>{indexOfFirstLog + 1}</strong> - <strong>{Math.min(indexOfLastLog, attackLogs.length)}</strong> 条
                </div>
                <Space>
                  <Button
                    type="primary"
                    icon={<RobotOutlined />}
                    onClick={handleAIAnalysisClick}
                  >
                    AI分析
                  </Button>
                  <Button
                    danger
                    icon={<FilePdfOutlined />}
                    onClick={handleExportClick}
                    loading={exportLoading}
                  >
                    {exportLoading ? '生成中...' : '生成报告'}
                  </Button>
                </Space>
              </div>
            )}
            
            {attackLogs.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '50px 20px', 
                color: '#8c8c8c',
                background: '#fafafa',
                borderRadius: '6px',
                border: '1px dashed #d9d9d9'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px', color: '#d9d9d9' }}>🔍</div>
                <div style={{ fontSize: '16px', marginBottom: '8px', color: '#595959' }}>
                  未找到 <strong style={{ color: '#262626' }}>{ip}</strong> 的攻击日志
                </div>
                <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
                  该IP地址可能没有遭受攻击，或者攻击记录已过期
                </div>
              </div>
            ) : (
              <>
                <Table
                  columns={columns}
                  dataSource={currentLogs}
                  pagination={false}
                  scroll={{ x: 1800 }}
                  size="middle"
                  bordered
                  rowKey="id"
                  style={{ borderRadius: '6px', overflow: 'hidden' }}
                />
                
                {totalPages > 1 && (
                  <div style={{ 
                    marginTop: '20px', 
                    textAlign: 'center',
                    padding: '16px',
                    background: '#fafafa',
                    borderRadius: '6px'
                  }}>
                    <Pagination
                      current={currentPage}
                      total={attackLogs.length}
                      pageSize={logsPerPage}
                      onChange={handlePageChange}
                      showSizeChanger={false}
                      showQuickJumper
                      showTotal={(total, range) => 
                        `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`
                      }
                    />
                  </div>
                )}
              </>
            )}
          </Card>
        )}

        {/* 导出确认弹窗 */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ExclamationCircleOutlined style={{ color: '#faad14' }} />
              <span>确认生成报告</span>
            </div>
          }
          open={showExportModal}
          onCancel={() => setShowExportModal(false)}
          footer={[
            <Button key="cancel" onClick={() => setShowExportModal(false)}>
              取消
            </Button>,
            <Button key="confirm" type="primary" danger onClick={handleExportConfirm}>
              确认生成
            </Button>
          ]}
          width={500}
        >
          <div style={{ padding: '16px 0' }}>
            <p style={{ marginBottom: '16px', fontSize: '16px' }}>您确定要生成攻击日志报告吗？</p>
            <div style={{ 
              background: '#fff7e6', 
              border: '1px solid #ffd591',
              borderRadius: '6px',
              padding: '16px'
            }}>
              <p style={{ marginBottom: '12px', color: '#d48806', fontWeight: '500' }}>
                ⚠️ 重要提示：
              </p>
              <ul style={{ 
                margin: 0, 
                paddingLeft: '20px', 
                color: '#8c8c8c',
                fontSize: '14px',
                lineHeight: '1.6'
              }}>
                <li>本报告属于内部文件，请勿对外传播</li>
                <li>报告包含真实性证明，可作为证据提交公安机关</li>
                <li>请妥善保管报告文件，避免泄露</li>
                <li>如需要提交给第三方，请先获得相关授权</li>
              </ul>
            </div>
          </div>
        </Modal>
      </div>

      {/* AI分析确认对话框 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <RobotOutlined style={{ color: '#1890ff' }} />
            <span>确认AI分析</span>
          </div>
        }
        open={showAnalysisConfirm}
        onCancel={() => setShowAnalysisConfirm(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowAnalysisConfirm(false)}>
            取消
          </Button>,
          <Button key="confirm" type="primary" onClick={handleConfirmAnalysis}>
            确认分析
          </Button>
        ]}
        width={500}
      >
        <div style={{ padding: '16px 0' }}>
          <p style={{ marginBottom: '16px', fontSize: '16px' }}>
            您确定要对 <strong style={{ color: '#1890ff' }}>{ip}</strong> 进行AI攻击分析吗？
          </p>
          <div style={{ 
            background: '#e6f7ff', 
            border: '1px solid #91d5ff',
            borderRadius: '6px',
            padding: '16px'
          }}>
            <div style={{ color: '#0050b3', fontSize: '14px', lineHeight: '1.6' }}>
              <strong>分析说明：</strong>
              <ul style={{ marginTop: '8px', marginBottom: 0, paddingLeft: '20px' }}>
                <li>分析时间范围：近三天</li>
                <li>AI将识别攻击模式和威胁等级</li>
                <li>提供针对性的防护建议</li>
                <li>分析过程可能需要几秒钟时间</li>
              </ul>
            </div>
          </div>
        </div>
      </Modal>

      {/* AI分析模态框 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <RobotOutlined style={{ color: '#1890ff' }} />
            <span>AI攻击分析（近三天数据）</span>
          </div>
        }
        open={aiAnalysisModal.visible}
        onCancel={() => setAiAnalysisModal({ visible: false, loading: false, analyzing: false, data: null })}
        footer={
          aiAnalysisModal.analyzing ? null : [
            <Button key="close" onClick={() => setAiAnalysisModal({ visible: false, loading: false, analyzing: false, data: null })}>
              关闭
            </Button>,
            aiAnalysisModal.data && (
              <Button
                key="export"
                type="primary"
                icon={<DownloadOutlined />}
                onClick={exportAnalysisPDF}
              >
                导出PDF
              </Button>
            )
          ]
        }
        width={900}
        closable={!aiAnalysisModal.analyzing}
        maskClosable={!aiAnalysisModal.analyzing}
      >
        {aiAnalysisModal.analyzing ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: '24px', fontSize: '16px', color: '#595959' }}>
              AI正在分析近三天的攻击数据...
            </div>
            <div style={{ marginTop: '12px', fontSize: '14px', color: '#8c8c8c' }}>
              请稍候，这可能需要几十秒钟时间
            </div>
          </div>
        ) : aiAnalysisModal.data ? (
          <Spin spinning={aiAnalysisModal.loading}>
            <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {/* 缓存提示 */}
              {aiAnalysisModal.data.fromCache && (
                <div style={{ 
                  marginBottom: 16, 
                  padding: '12px 16px',
                  background: '#fff7e6',
                  border: '1px solid #ffd591',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{ fontSize: '16px' }}>⚡</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', color: '#d46b08', fontWeight: 500 }}>
                      缓存数据
                    </div>
                    <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '2px' }}>
                      {aiAnalysisModal.data.cacheInfo}
                    </div>
                  </div>
                </div>
              )}

              {/* 基本信息 */}
              <Card size="small" style={{ marginBottom: 16 }}>
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="目标IP">{aiAnalysisModal.data.targetIP}</Descriptions.Item>
                  <Descriptions.Item label="响应时间">
                    {aiAnalysisModal.data.responseTime}ms
                  </Descriptions.Item>
                  <Descriptions.Item label="分析范围" span={2}>
                    {dayjs(aiAnalysisModal.data.timeRange?.startDate).format('YYYY-MM-DD')} 至 {dayjs(aiAnalysisModal.data.timeRange?.endDate).format('YYYY-MM-DD')}
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              {/* 攻击统计 */}
              {aiAnalysisModal.data.statistics && (
                <Card title="攻击统计" size="small" style={{ marginBottom: 16 }}>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Statistic
                        title="总攻击次数"
                        value={aiAnalysisModal.data.statistics.totalAttacks || 0}
                        prefix={<WarningOutlined />}
                        styles={{ content: { color: '#ff4d4f' } }}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="最大流量"
                        value={aiAnalysisModal.data.statistics.maxAttackValue || 0}
                        suffix="Gbps"
                        styles={{ content: { color: '#fa8c16' } }}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="平均流量"
                        value={aiAnalysisModal.data.statistics.avgAttackValue || 0}
                        suffix="Gbps"
                      />
                    </Col>
                  </Row>
                  <Row gutter={16} style={{ marginTop: 16, display: 'flex', alignItems: 'stretch' }}>
                    <Col span={12} style={{ display: 'flex' }}>
                      <div style={{ 
                        padding: '12px', 
                        background: '#fff7e6', 
                        borderRadius: '4px',
                        border: '1px solid #ffd591',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        width: '100%'
                      }}>
                        <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '4px' }}>攻击天数</div>
                        <div style={{ fontSize: '20px', fontWeight: 600, color: '#fa8c16' }}>
                          {aiAnalysisModal.data.statistics.attackDays} 天
                        </div>
                      </div>
                    </Col>
                    <Col span={12} style={{ display: 'flex' }}>
                      <div style={{ 
                        padding: '12px', 
                        background: '#fff2f0', 
                        borderRadius: '4px',
                        border: '1px solid #ffccc7',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        width: '100%'
                      }}>
                        <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '4px' }}>攻击高峰</div>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#ff4d4f', lineHeight: '1.4' }}>
                          {aiAnalysisModal.data.statistics.peakAttackTime}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card>
              )}

              {/* AI分析结果 */}
              {aiAnalysisModal.data.aiAnalysis && (
                <>
                  {/* 威胁等级 */}
                  {aiAnalysisModal.data.aiAnalysis.threatLevel && (
                    <Card size="small" style={{ marginBottom: 16 }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '14px', color: '#8c8c8c', marginBottom: '8px' }}>威胁等级</div>
                        <div style={{ 
                          display: 'inline-block',
                          padding: '8px 24px',
                          fontSize: '24px',
                          fontWeight: 'bold',
                          borderRadius: '4px',
                          background: aiAnalysisModal.data.aiAnalysis.threatLevel === '严重' ? '#fff2f0' : 
                                     aiAnalysisModal.data.aiAnalysis.threatLevel === '高' ? '#fff7e6' : '#f6ffed',
                          color: aiAnalysisModal.data.aiAnalysis.threatLevel === '严重' ? '#ff4d4f' : 
                                 aiAnalysisModal.data.aiAnalysis.threatLevel === '高' ? '#fa8c16' : '#52c41a'
                        }}>
                          {aiAnalysisModal.data.aiAnalysis.threatLevel}
                        </div>
                      </div>
                    </Card>
                  )}

                  {/* 攻击类型 */}
                  {aiAnalysisModal.data.aiAnalysis.attackTypes && aiAnalysisModal.data.aiAnalysis.attackTypes.length > 0 && (
                    <Card title="攻击类型" size="small" style={{ marginBottom: 16 }}>
                      <Space wrap>
                        {aiAnalysisModal.data.aiAnalysis.attackTypes.map((type, idx) => (
                          <span key={idx} style={{ 
                            padding: '4px 12px', 
                            borderRadius: '4px',
                            background: '#fff2f0',
                            color: '#ff4d4f',
                            fontSize: '14px',
                            fontWeight: 500
                          }}>
                            {type}
                          </span>
                        ))}
                      </Space>
                    </Card>
                  )}

                  <Collapse 
                    defaultActiveKey={['1', '2', '3', '4', '5']} 
                    style={{ marginBottom: 16 }}
                    items={[
                      aiAnalysisModal.data.aiAnalysis.attackCharacteristics && {
                        key: '1',
                        label: '攻击特征分析',
                        children: (
                          <Paragraph style={{ whiteSpace: 'pre-wrap' }}>
                            {aiAnalysisModal.data.aiAnalysis.attackCharacteristics}
                          </Paragraph>
                        )
                      },
                      aiAnalysisModal.data.aiAnalysis.attackPatterns && {
                        key: '2',
                        label: '攻击模式分析',
                        children: (
                          <Paragraph style={{ whiteSpace: 'pre-wrap' }}>
                            {aiAnalysisModal.data.aiAnalysis.attackPatterns}
                          </Paragraph>
                        )
                      },
                      aiAnalysisModal.data.aiAnalysis.trendAnalysis && {
                        key: '3',
                        label: '趋势分析',
                        children: (
                          <Paragraph style={{ whiteSpace: 'pre-wrap' }}>
                            {aiAnalysisModal.data.aiAnalysis.trendAnalysis}
                          </Paragraph>
                        )
                      },
                      aiAnalysisModal.data.aiAnalysis.overallAssessment && {
                        key: '4',
                        label: '总体评估',
                        children: (
                          <Paragraph style={{ whiteSpace: 'pre-wrap' }}>
                            {aiAnalysisModal.data.aiAnalysis.overallAssessment}
                          </Paragraph>
                        )
                      },
                      aiAnalysisModal.data.aiAnalysis.protectionSuggestions && {
                        key: '5',
                        label: '防护建议',
                        children: (
                          <Paragraph style={{ whiteSpace: 'pre-wrap' }}>
                            {aiAnalysisModal.data.aiAnalysis.protectionSuggestions}
                          </Paragraph>
                        )
                      }
                    ].filter(Boolean)}
                  />
                </>
              )}
            </div>
          </Spin>
        ) : (
          <Empty description="暂无分析数据" />
        )}
      </Modal>
    </div>
  );
};

export default UserAttackQuery;
